import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Avatar, Badge, Button, createTableColumn, DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridProps, DataGridRow, Label, Spinner, TableCellLayout, TableColumnDefinition, TableRowId } from '@fluentui/react-components';

// Component Functions
import * as ComponentFunctions from './PractitionerProtocolConsumption.List.Functions';

// models
import { ProtocolPatient } from '../../../../models/Entities/Protocols/ProtocolPatient';
import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { Practitioner } from '../../../../models/Entities/Practitioners/Practitioner';
import { IConsumptionStatusProps } from '../../../../commons/component/Inputs/ProtocolConsumptionStatusSelector/ProtocolConsumptionStatusSelector.Functions';
import { ProtocolConsumption } from '../../../../models/Entities/Protocols/ProtocolConsumption';
import { PaginationControlComponent } from '../../../../commons/component/PaginationControl/PaginationControl.Component';
import { Separator } from '@fluentui/react';
import moment from 'moment';
import { DeleteRegular, EditRegular, Warning16Regular } from '@fluentui/react-icons';
import { conversionLabel, conversionOf, feeBreakdown, formatPercentage, hasFeeBreakdown, rateTypeLabel } from '../../../../commons/fee/PractitionerFee.Logic';
import PractitionerConsumptionDetailsMiniComponent from '../PractitionerConsumptionDetailsMini/PractitionerConsumptionDetailsMini.Component';
import { PractitionerConsumption } from '../../../../models/Entities/Practitioners/PractitionerConsumption';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import PractitionerConsumptionMenuComponent from './PractitionerConsumption.Menu/PractitionerConsumption.Menu.Component';
import PractitionerConsumptionFilterComponent, { IPractitionerConsumptionsFilters } from './PractitionerConsumption.Filters/PractitionerConsumption.Filter.Component';
import PractitionerConsumptionToSettleComponent from '../PractitionerConsumptionToSettle/PractitionerConsumptionToSettle.Component'; // Import the component
import PractitionerConsumptionFormComponent from '../PractitionerConsumptionForm/PractitionerConsumption.Form.Component';
import { formatCurrencyAmount } from '../PractitionerConsumptionCurrency';

// Export to Excel PractitionerConsumptions Service
import { ExportToExcelLink } from '../../../ExportToExcel/ExportToExcelLink.Component';
import { ExportConsumptionByPractitionerWrapper } from '../../../ExportToExcel/Wrappers/ExportConsumptionByPractitionerWrapper';
import { IConsumptionsFilters } from '../../../../models/Filters/ConsumptionsFilters';
import { ProtocolConsumptionRealizeTypeEnum } from '../../../../models/Enums/ProtocolConsumptionRealizeTypeEnum';
import { PatientVisitStatusEnum } from '../../../../models/Enums/PatientVisitStatusEnum';

// Styles
import './PractitionerProtocolConsumption.List.Styles.css';

// ── Settlement group summary ───────────────────────────────────────────────
// Totals per currency + status breakdown, so a collapsed settlement still says
// how much it is worth and how its consumptions are distributed.
const summarizeConsumptions = (items: PractitionerConsumption[]) => {
    const byCurrency = new Map<string, { currency: string; total: number; pending: number }>();
    const byStatus = new Map<number, number>();

    items.forEach((item) => {
        const currency = (item.currencyCode || '').trim();
        const bucket = byCurrency.get(currency) ?? { currency, total: 0, pending: 0 };
        bucket.total += item.amount ?? 0;
        bucket.pending += item.pendingAmount ?? 0;
        byCurrency.set(currency, bucket);

        byStatus.set(item.status, (byStatus.get(item.status) ?? 0) + 1);
    });

    return {
        totalsByCurrency: Array.from(byCurrency.values())
            .sort((a, b) => a.currency.localeCompare(b.currency)),
        statusCounts: Array.from(byStatus.entries())
            .map(([status, count]) => ({ status, count }))
            .sort((a, b) => b.count - a.count),
    };
};

// How the visit was carried out — `realizedType`, 1-based.
const REALIZE_TYPES: Record<number, { label: string; className: string }> = {
    [ProtocolConsumptionRealizeTypeEnum.RealizedRemotely]:   { label: 'Remota', className: 'realized--remote' },
    [ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace]: { label: 'Presencial', className: 'realized--onsite' },
    [ProtocolConsumptionRealizeTypeEnum.RealizedAtHome]:     { label: 'Domicilio', className: 'realized--home' },
    [ProtocolConsumptionRealizeTypeEnum.NotRealized]:        { label: 'No realizada', className: 'realized--none' },
};

// State of the visit itself — `visitStatus`, 0-based. Labels match
// GetVisitStatusDescription in Protocol.Activity.Functions.
const VISIT_STATUSES: Record<number, { label: string; className: string }> = {
    [PatientVisitStatusEnum.Started]:       { label: 'Iniciada', className: 'visit--started' },
    [PatientVisitStatusEnum.Completed]:     { label: 'Completada', className: 'visit--completed' },
    [PatientVisitStatusEnum.Skipped]:       { label: 'Salteada', className: 'visit--skipped' },
    [PatientVisitStatusEnum.Failed]:        { label: 'Falla de selección', className: 'visit--failed' },
    [PatientVisitStatusEnum.Changed]:       { label: 'Modificada', className: 'visit--changed' },
    [PatientVisitStatusEnum.AutoCompleted]: { label: 'Autocompletada', className: 'visit--autocompleted' },
};

// Within a settlement, consumptions are split per protocol so each one shows
// its own subtotal. Sorted by name to keep the order stable across refetches.
// El título sale del protocolo cargado en el store: el `protocolName` que
// manda el API es el número de protocolo, no su descripción.
const groupByProtocol = (items: PractitionerConsumption[], titleOf: (protocolId: number) => string | undefined) => {
    const byProtocol = new Map<number, { id: number; name: string; items: PractitionerConsumption[] }>();

    items.forEach((item) => {
        const existing = byProtocol.get(item.protocolId);
        if (existing) {
            existing.items.push(item);
        } else {
            byProtocol.set(item.protocolId, {
                id: item.protocolId,
                name: titleOf(item.protocolId) || item.protocolName || `#${item.protocolId}`,
                items: [item],
            });
        }
    });

    return Array.from(byProtocol.values())
        .map((group) => ({ ...group, summary: summarizeConsumptions(group.items) }))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
};

// Tooltip nativo con la cadena completa del honorario: cabe en un title y no
// depende de popovers, que dentro de la grilla no sobreviven al scroll.
const feeTitle = (item: PractitionerConsumption): string => {
    const b = feeBreakdown(item);
    const fmt = (value: number) => formatCurrencyAmount(value, item.currencyCode);
    const lines = [
        `Facturado al sponsor: ${fmt(b.source)}`,
        `− Retención: ${fmt(b.withholding)}`,
        `− Costo descontado: ${fmt(b.discountedCost)}`,
        `= Base de cálculo: ${fmt(b.base)}`,
        `× ${formatPercentage(b.percentage)} = Honorario: ${fmt(b.fee)}`,
    ];
    if (b.hasManualAdjustment) lines.push('El honorario no coincide con base × %: tiene ajuste manual o cotización.');
    const conversion = conversionOf(item);
    if (conversion) {
        const type = rateTypeLabel(conversion.rateType);
        lines.push(`Convertido desde ${conversionLabel(conversion, formatCurrencyAmount)}${type ? ` (${type})` : ''}`);
    }
    return lines.join('\n');
};

// Shared by the settlement groups and by the "no settlement" block
const GroupSummary = ({ totalsByCurrency, statusCounts }: ReturnType<typeof summarizeConsumptions>) => (
    <>
        <span className="pcg-totals">
            {totalsByCurrency.map((total) => (
                <span className="pcg-total" key={total.currency || 'unknown'}>
                    <span className="pcg-total__label">{total.currency ? total.currency.toUpperCase() : 'Sin moneda'}</span>
                    <span className="pcg-total__value">{formatCurrencyAmount(total.total, total.currency)}</span>
                    {total.pending > 0 && (
                        <span className="pcg-total__pending">
                            Pendiente {formatCurrencyAmount(total.pending, total.currency)}
                        </span>
                    )}
                </span>
            ))}
        </span>
        <span className="pcg-statuses">
            {statusCounts.map(({ status, count }) => {
                const descriptor = ComponentFunctions.GetStatusDescription(status);
                return (
                    <span className={`pcg-status ${descriptor.class}`} key={status}>
                        <span className="pcg-status__dot" />
                        {descriptor.description}
                        <span className="pcg-status__count">{count}</span>
                    </span>
                );
            })}
        </span>
    </>
);

const PractitionerProtocolConsumptionListComponent = (props: ComponentFunctions.Props) => {

    const toolbarRef = useRef<HTMLDivElement | null>(null);
    const [isToolbarSticky, setIsToolbarSticky] = useState(false);

    // Settlement groups open by default; only the ones the user collapses are
    // tracked, so re-filtering does not reopen what they closed.
    const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
    const [isUnlinkedOpen, setIsUnlinkedOpen] = useState(true);

    // Protocol sections are keyed "<settlement>:<protocol>" so the same protocol
    // can be open in one settlement and collapsed in another.
    const [collapsedProtocols, setCollapsedProtocols] = useState<string[]>([]);

    const toggleCollapsed = (
        setter: React.Dispatch<React.SetStateAction<string[]>>,
        value: string,
    ) => {
        setter((current) =>
            current.includes(value)
                ? current.filter((id) => id !== value)
                : [...current, value]
        );
    };

    const toggleGroup = (value: string) => toggleCollapsed(setCollapsedGroups, value);
    const toggleProtocol = (value: string) => toggleCollapsed(setCollapsedProtocols, value);

    // Pins the toolbar under the page header (and under the to-settle strip),
    // mirroring ConsumptionList so both screens scroll the same way.
    useEffect(() => {
        const toolbar = toolbarRef.current;
        if (!toolbar) {
            return;
        }

        const updateToolbarStickyState = () => {
            const stickyOffset = Number.parseFloat(
                getComputedStyle(document.documentElement).getPropertyValue('--page-header-height')
            ) || 56;
            setIsToolbarSticky(toolbar.getBoundingClientRect().top <= stickyOffset + 1);
        };

        updateToolbarStickyState();
        window.addEventListener('scroll', updateToolbarStickyState, { passive: true });
        window.addEventListener('resize', updateToolbarStickyState);

        return () => {
            window.removeEventListener('scroll', updateToolbarStickyState);
            window.removeEventListener('resize', updateToolbarStickyState);
        };
    }, []);

    const [selectedPatients, setSelectedPatients] = useState<ProtocolPatient[]>([]);
    const [selectedContractConcepts, setSelectedContractConcepts] = useState<ContractConcept[]>([]);
    const [selectedCurrency, setSelectedCurrency] = useState<string>('');
    const [selectedContractStages, setSelectedContractStages] = useState<ContractStage[]>([]);
    const [selectedPractitioners, setSelectedPractitioners] = useState<Practitioner[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<IConsumptionStatusProps[]>([]);
    const [selectedProtocols, setSelectedProtocols] = useState<Protocol[]>([]);
    const [filterText, setFilterText] = useState<string>();

    const [selectedConsumption, setSelectedConsumption] = useState<PractitionerConsumption | null>(null);

    const [currentFilters, setCurrentFilter] = useState<IConsumptionsFilters>({
        FilterText: '',
        SelectedConsumptions: [],
        SelectedProtocols: selectedProtocols,
        SelectedProtocolPatients: [],
        SelectedContractConcepts: [],
        SelectedCurrencys: selectedCurrency,
        SelectedContractStages: [],
        SelectedPractitioners: [],
        SelectedStatus: selectedStatus,
        SelectedSettlements: [],
        SelectedDateRange: [undefined, undefined],
        showGroupedByVisit: false,
        GroupId: undefined,
    });

    // Row Selection
    const [selectedRows, setSelectedRows] = useState(new Set<TableRowId>());

    const onSelectionChange: DataGridProps["onSelectionChange"] = (e, data) => {
        setSelectedRows(data.selectedItems);
        console.log('filas seleccionadas', data.selectedItems);
    };

    const onDeleteConsumption = (consumption: PractitionerConsumption) => {
        props.PractitionerId && props.DeletePractitionerConsumption(props.PractitionerId, consumption.id);
    }

    // information panel
    const [isInformationPanelOpen, setIsInformationPanelOpen] = useState(false);
    // create or edit form
    const [isConsumptionFormOpen, setIsConsumptionFormOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);

    // on close form
    const onInformationPanelDismiss = () => {
        setSelectedConsumption(null);
        setIsInformationPanelOpen(false);
    }

    // on close form
    const onConsumptionFormDismiss = () => {
        setSelectedConsumption(null);
        setIsConsumptionFormOpen(false);
    }

    const onEditDebt = (consumption: PractitionerConsumption) => { 
        setSelectedConsumption(consumption);
        setIsInformationPanelOpen(true);
    }

    useEffect(() => {
        if (props.Practitioner.userId) {
            props.GetAllPractitionerConsumptionsByPractitioner(props.Practitioner.userId, 1, 100);
        }
    }, [props.Practitioner?.userId]);

    useEffect(() => {
        if (!props.Practitioner?.userId) {
            props.GetAllPractitioners(1, 1000, undefined);
        }
    },
        [
            props.Practitioner?.userId === undefined,
        ]);

    useEffect(() => {
        if (props.Practitioner?.userId) {
            props.GetPractitionerConsumptionsToSettle(props.Practitioner.userId);
            props.GetAllPractitionerConsumptionsByPractitioner(
                props.Practitioner?.userId,
                currentPage,
                pageSize,
                {
                    SelectedConsumptions: [],
                    FilterText: filterText,
                    SelectedContractConcepts: selectedContractConcepts,
                    SelectedCurrencys: selectedCurrency,
                    SelectedProtocolPatients: selectedPatients,
                    SelectedPractitioners: selectedPractitioners,
                    SelectedProtocols: selectedProtocols,
                    SelectedContractStages: selectedContractStages,
                    SelectedStatus: selectedStatus,
                    SelectedSettlements: [],
                    SelectedDateRange: currentFilters.SelectedDateRange,
                    ignoreVisitConsumption: true,
                    showGroupedByVisit: currentFilters.showGroupedByVisit,
                    GroupId: undefined,
                }
            );
            //props.GetAllProtocolContractAssociations(props.protocol?.id);
        }
    }, [
        props.Practitioner?.userId,
        selectedPatients,
        selectedContractConcepts,
        selectedCurrency,
        selectedContractStages,
        selectedPractitioners,
        selectedProtocols,
        selectedStatus,
        filterText,
        currentPage,
        pageSize
    ]);

    const onRequestUpdate = () => {
        if (props.Practitioner?.userId) {
            props.GetPractitionerConsumptionsToSettle(props.Practitioner?.userId);
            props.GetAllPractitionerConsumptionsByPractitioner(
                props.Practitioner?.userId,
                1,
                pageSize,
                {
                    SelectedConsumptions: [],
                    FilterText: filterText,
                    SelectedProtocolPatients: selectedPatients,
                    SelectedContractConcepts: [],
                    SelectedCurrencys: selectedCurrency,
                    SelectedContractStages: [],
                    SelectedPractitioners: selectedPractitioners,
                    SelectedStatus: selectedStatus,
                    SelectedProtocols: selectedProtocols,
                    SelectedSettlements: [],
                    SelectedDateRange: currentFilters.SelectedDateRange,
                    ignoreVisitConsumption: false,
                    showGroupedByVisit: currentFilters.showGroupedByVisit,
                    GroupId: undefined,
                } as IConsumptionsFilters
            );
        }
    }


    const columns: (TableColumnDefinition<PractitionerConsumption> | false)[] = [
        createTableColumn<PractitionerConsumption>({
            columnId: "status",
            compare: (a, b) => {
                return a.status - b.status;
            },
            renderHeaderCell: () => {
                return "Estado";
            },
            renderCell: (item: PractitionerConsumption) => {
                return (
                    <TableCellLayout>
                        {item.isChangingStatus || item.isSaving ? (
                            <Spinner size="extra-tiny" />
                        ) : (
                            <div className={'status-mark ' + ComponentFunctions.GetStatusDescription(item.status).class}></div>
                        )}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "realizedType",
            compare: (a, b) => {
                return (a.realizedType ?? 0) - (b.realizedType ?? 0);
            },
            renderHeaderCell: () => {
                return "Modalidad";
            },
            renderCell: (item: PractitionerConsumption) => {
                const realized = REALIZE_TYPES[item.realizedType];
                if (!realized) {
                    return <TableCellLayout><span className="cell-muted">—</span></TableCellLayout>;
                }
                return (
                    <TableCellLayout>
                        <span className={`cell-realized ${realized.className}`} title={realized.label}>
                            {realized.label}
                        </span>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "visitStatus",
            compare: (a, b) => {
                return (a.visitStatus ?? 0) - (b.visitStatus ?? 0);
            },
            renderHeaderCell: () => {
                return "Estado HC";
            },
            renderCell: (item: PractitionerConsumption) => {
                const visit = VISIT_STATUSES[item.visitStatus];
                if (!visit) {
                    return <TableCellLayout><span className="cell-muted">—</span></TableCellLayout>;
                }
                return (
                    <TableCellLayout>
                        <span className={`cell-visit ${visit.className}`} title={visit.label}>
                            <span className="cell-visit__dot" />
                            {visit.label}
                        </span>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "protocol",
            compare: (a, b) => {
                return a.protocolId - b.protocolId;
            },
            renderHeaderCell: () => {
                return "Protocolo";
            },
            renderCell: (item: PractitionerConsumption) => {
                return (
                    <TableCellLayout>
                        <Label>{props.GetProtocolById(item.protocolId)?.title}</Label>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "stage",
            compare: (a, b) => {
                return a.stageName?.localeCompare(b.stageName);
            },
            renderHeaderCell: () => {
                return "Visita";
            },
            renderCell: (item: PractitionerConsumption) => {
                return (
                    <TableCellLayout>
                        <Label>{item.stageName}</Label>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "patient",
            compare: (a, b) => {
                return a.patientNumberInProtocol?.localeCompare(b.patientNumberInProtocol);
            },
            renderHeaderCell: () => {
                return "Paciente";
            },
            renderCell: (item: PractitionerConsumption) => {
                return (
                    <TableCellLayout>
                        <div className="cell-patient">
                            <Label>{item.patientNumberInProtocol}</Label>
                            {/* HC vinculada por MedicalProtocol: sirve para cruzar con la historia clínica */}
                            {item.medicalRecordNumber && (
                                <span className="cell-patient__hc" title="Historia clínica">HC {item.medicalRecordNumber}</span>
                            )}
                        </div>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "concept",
            compare: (a, b) => {
                return a.serviceName.localeCompare(b.serviceName);
            },
            renderHeaderCell: () => {
                return "Concepto";
            },
            renderCell: (item: PractitionerConsumption) => {
                const warning = item.markedAsHaveWarnings ? (item.warningsComments || '').trim() || 'Este consumo tiene una advertencia.' : '';
                return (
                    <TableCellLayout>
                        <div className="cell-concept">
                            <Label>{item.serviceName}</Label>
                            {warning && (
                                <span className="cell-warning" title={warning} aria-label={warning}>
                                    <Warning16Regular /> Con advertencia
                                </span>
                            )}
                        </div>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "duedate",
            compare: (a, b) => {
                return new Date(a.serviceDueDate).getTime() - new Date(b.serviceDueDate).getTime();
            },
            renderHeaderCell: () => {
                return "Fecha realización";
            },
            renderCell: (item: PractitionerConsumption) => {
                return (
                    <TableCellLayout>
                        {item.serviceDueDate && moment(item.serviceDueDate).format("DD/MM/YYYY hh:mm a")}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "producer",
            // compare: (a, b) => {
            //     return props.GetPersonByIdentifier(a.producer)?.fullName!.localeCompare(props.GetPersonByIdentifier(b.producer)!.fullName!);
            // },
            renderHeaderCell: () => {
                return "Realizado por";
            },
            renderCell: (item: PractitionerConsumption) => {
                return (
                    <TableCellLayout media={(
                        <Avatar
                            aria-label={item.practitionerId}
                            name={item.practitionerId ? props.GetPersonByIdentifier(item.practitionerId)?.fullName : ''}
                            color="colorful" />
                    )}>
                        {item.practitionerId ? props.GetPersonByIdentifier(item.practitionerId)?.fullName : ''}
                    </TableCellLayout>
                );
            },
            compare: function (a: any, b: any): number {
                throw new Error('Function not implemented.');
            }
        }),
        // createTableColumn<ProtocolConsumption>({
        //     columnId: "unitAmount",
        //     compare: (a, b) => {
        //         return (a.unitAmount - b.unitAmount);
        //     },
        //     renderHeaderCell: () => {
        //         return "Monto unitario";
        //     },
        //     renderCell: (item: ProtocolConsumption) => {
        //         return (
        //             <TableCellLayout>
        //                 <Text>{item.unitAmount?.toLocaleString('es-AR', { style: 'currency', currency: item.currencyCode?.toUpperCase() }) || '0.00'}</Text>
        //             </TableCellLayout>
        //         )
        //     },
        // }),
        // createTableColumn<ProtocolConsumption>({
        //     columnId: "Amount",
        //     compare: (a, b) => {
        //         return props.CurrentProvider ? (a.costAmount - b.costAmount) : (a.unitAmount - b.unitAmount);
        //     },
        //     renderHeaderCell: () => {
        //         return "Monto";
        //     },
        //     renderCell: (item: ProtocolConsumption) => {
        //         return (
        //             <TableCellLayout>
        //                 <strong>{props.ProviderId ? item.costAmount?.toLocaleString('es-AR', { style: 'currency', currency: item.costCurrencyCode?.toUpperCase() ?? "ARS" }) ?? '0.00' : item.amount?.toLocaleString('es-AR', { style: 'currency', currency: item.currencyCode?.toUpperCase() }) ?? '0.00'}</strong>
        //             </TableCellLayout>
        //         )
        //     },
        // }),
        // createTableColumn<ProtocolConsumption>({
        //     columnId: "addingsAmount",
        //     compare: (a, b) => {
        //         return (a.totalValues - b.totalValues);
        //     },
        //     renderHeaderCell: () => {
        //         return "Montos adicionales";
        //     },
        //     renderCell: (item: ProtocolConsumption) => {
        //         return (
        //             <TableCellLayout>
        //                 <strong>{item.totalValues?.toLocaleString('es-AR', { style: 'currency', currency: item.currencyCode?.toUpperCase() }) || '0.00'}</strong>
        //             </TableCellLayout>
        //         )
        //     },
        // }),
        // createTableColumn<ProtocolConsumption>({
        //     columnId: "withholdingAmount",
        //     compare: (a, b) => {
        //         return (a.withHolingAmount - b.withHolingAmount);
        //     },
        //     renderHeaderCell: () => {
        //         return "withholding";
        //     },
        //     renderCell: (item: ProtocolConsumption) => {
        //         return (
        //             <TableCellLayout>
        //                 <Text>{item.withHolingAmount?.toLocaleString('es-AR', { style: 'currency', currency: item.currencyCode?.toUpperCase() }) || '0.00'} | ({item.withHolingPercentage + '%'})</Text>
        //             </TableCellLayout>
        //         )
        //     },
        // }),
        createTableColumn<PractitionerConsumption>({
            columnId: "feeBase",
            compare: (a, b) => {
                return feeBreakdown(a).base - feeBreakdown(b).base;
            },
            renderHeaderCell: () => {
                return "Base de cálculo";
            },
            renderCell: (item: PractitionerConsumption) => {
                // Sin base informada no hay nada que explicar: consumo manual o anterior al cambio.
                if (!hasFeeBreakdown(item)) {
                    return <TableCellLayout><span className="cell-muted">—</span></TableCellLayout>;
                }
                const breakdown = feeBreakdown(item);
                return (
                    <TableCellLayout>
                        <span className="cell-fee-base" title={feeTitle(item)}>
                            <span className="cell-fee-base__value">{formatCurrencyAmount(breakdown.base, item.currencyCode)}</span>
                            {breakdown.percentage > 0 && (
                                <span className="cell-fee-base__pct">× {formatPercentage(breakdown.percentage)}</span>
                            )}
                        </span>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "totalAmount",
            compare: (a, b) => {
                return (a.amount - b.amount);
            },
            renderHeaderCell: () => {
                return "Honorario";
            },
            renderCell: (item: PractitionerConsumption) => {
                const withBreakdown = hasFeeBreakdown(item);
                const adjusted = withBreakdown && feeBreakdown(item).hasManualAdjustment;
                const conversion = conversionOf(item);
                const conversionType = conversion ? rateTypeLabel(conversion.rateType) : undefined;
                return (
                    <TableCellLayout>
                        <span className="cell-fee">
                            <strong
                                className={adjusted ? 'cell-fee--adjusted' : undefined}
                                title={withBreakdown || conversion ? feeTitle(item) : undefined}
                            >
                                {formatCurrencyAmount(item.amount, item.currencyCode)}
                            </strong>
                            {/* Cotización aplicada: importe original × tipo de cambio */}
                            {conversion && (
                                <span className="cell-fee__conv" title={conversionType ? `Convertido con ${conversionType}` : 'Convertido'}>
                                    {conversionLabel(conversion, formatCurrencyAmount)}
                                </span>
                            )}
                        </span>
                    </TableCellLayout>
                )
            },
        }),
        // createTableColumn<ProtocolConsumption>({
        //     columnId: "Settled",
        //     compare: (a, b) => {
        //         return (a.payeeAmount - b.payeeAmount);
        //     },
        //     renderHeaderCell: () => {
        //         return "Pagado";
        //     },
        //     renderCell: (item: ProtocolConsumption) => {
        //         return (
        //             <TableCellLayout>
        //                 <strong>{item.payeeAmount?.toLocaleString('es-AR', { style: 'currency', currency: item.currencyCode?.toUpperCase() }) || '0.00'}</strong>
        //             </TableCellLayout>
        //         )
        //     },
        // }),
        createTableColumn<PractitionerConsumption>({
            columnId: "pending",
            compare: (a, b) => {
                return (a.pendingAmount - b.pendingAmount);
            },
            renderHeaderCell: () => {
                return "Pendiente";
            },
            renderCell: (item: PractitionerConsumption) => {
                return (
                    <TableCellLayout>
                        <strong>{formatCurrencyAmount(item.pendingAmount, item.currencyCode)}</strong>
                    </TableCellLayout>
                )
            },
        }),
        createTableColumn<PractitionerConsumption>({
            columnId: "actions",
            renderHeaderCell: () => {
                return "Actions";
            },
            renderCell: (item: PractitionerConsumption) => {
                return (
                    <>
                        <Button appearance="subtle" aria-label="Edit" icon={<EditRegular />} onClick={(event) => { event.stopPropagation(); onEditDebt(item); }} />
                        {![1, 2, 3, 6].includes(item.status) && <Button appearance="subtle" aria-label="Delete" icon={<DeleteRegular />} onClick={(event) => { event.stopPropagation(); onDeleteConsumption(item); }} />}
                    </>
                );
            },
            compare: function (a: any, b: any): number {
                throw new Error('Function not implemented.');
            }
        }),
    ];

    const columnSizingOptions = {
        status: {
            defaultWidth: 20,
            idealWidth: 20,
        },
        realizedType: {
            defaultWidth: 110,
            idealWidth: 110,
        },
        visitStatus: {
            defaultWidth: 145,
            idealWidth: 145,
        },
        stage: {
            defaultWidth: 130,
            idealWidth: 130,
        },
        patient: {
            defaultWidth: 180,
            idealWidth: 180,
        },
        concept: {
            defaultWidth: 180,
            idealWidth: 180,
        },
        duedate: {
            defaultWidth: 150,
            idealWidth: 150,
        },
        producer: {
            defaultWidth: 150,
            idealWidth: 150,
        },
        feeBase: {
            defaultWidth: 150,
            idealWidth: 150,
        },
        Amount: {
            defaultWidth: 100,
            idealWidth: 100,
        },
        actions: {
            defaultWidth: 100,
            idealWidth: 100,
            maxWidth: 50,
        },

    };

    const Forms = (
        <>
        </>
    );


    // Compose a single filter object for easier filter logic
    const filterProps = {
        filterText: filterText || '',
        selectedProtocols,
        selectedCurrency,
        selectedStatus,
        selectedPatients,
        selectedContractConcepts,
        selectedContractStages,
        selectedPractitioners
    };

    // Filtering logic for DataGrid
    const filteredConsumptions = (props.Consumptions || []).filter((consumption: PractitionerConsumption) => {
        return (
            (filterProps.selectedPatients.length === 0 || filterProps.selectedPatients.map((patient: ProtocolPatient) => patient.numberInProtocol).includes(consumption.patientNumberInProtocol)) &&
            (filterProps.selectedContractConcepts.length === 0 || filterProps.selectedContractConcepts.map((contractConcept: ContractConcept) => contractConcept?.concept?.conceptName).includes(consumption.conceptDescription)) &&
            (filterProps.selectedCurrency === undefined || filterProps.selectedCurrency === null || filterProps.selectedCurrency === '' || consumption.currencyCode === filterProps.selectedCurrency) &&
            (filterProps.selectedProtocols.length === 0 || filterProps.selectedProtocols.map((protocol: Protocol) => protocol.id).includes(consumption.protocolId)) &&
            (filterProps.selectedContractStages.length === 0 || filterProps.selectedContractStages.map((contractStage: ContractStage) => contractStage.stageName).includes(consumption.stageName)) &&
            (filterProps.selectedStatus.length === 0 || filterProps.selectedStatus.map((status: IConsumptionStatusProps) => status.id).includes(consumption.status)) &&
            (filterProps.filterText === undefined || filterProps.filterText === null || filterProps.filterText === '' || (consumption.serviceName?.toLowerCase() || '').includes(filterProps.filterText.toLowerCase()))
        );
    });

    // ── Per-protocol sections inside a settlement ──────────────────────────
    const renderProtocolGroups = (groupKey: string, items: PractitionerConsumption[]) => {
        const protocols = groupByProtocol(items, (protocolId) => props.GetProtocolById(protocolId)?.title);

        return (
            <Accordion
                multiple
                collapsible
                className="pcp-groups"
                openItems={protocols
                    .map((protocol) => `${groupKey}:${protocol.id}`)
                    .filter((key) => !collapsedProtocols.includes(key))}
                onToggle={(_, data) => toggleProtocol(String(data.value))}
            >
                {protocols.map((protocol) => {
                    const key = `${groupKey}:${protocol.id}`;
                    const isOpen = !collapsedProtocols.includes(key);
                    return (
                        <AccordionItem
                            key={key}
                            value={key}
                            className={`pcp-group${isOpen ? ' pcp-group--expanded' : ''}`}
                        >
                            <AccordionHeader expandIconPosition="end">
                                <span className="pcp-header">
                                    <span className="pcp-name" title={protocol.name}>{protocol.name}</span>
                                    <span className="pcp-count" title={`${protocol.items.length} consumos`}>{protocol.items.length}</span>
                                    <span className="pcp-totals">
                                        <span className="pcp-totals__label">Subtotal</span>
                                        {protocol.summary.totalsByCurrency.map((total) => (
                                            <span className="pcp-total" key={total.currency || 'unknown'}>
                                                <span className="pcp-total__label">
                                                    {total.currency ? total.currency.toUpperCase() : 'Sin moneda'}
                                                </span>
                                                <span className="pcp-total__value">
                                                    {formatCurrencyAmount(total.total, total.currency)}
                                                </span>
                                            </span>
                                        ))}
                                    </span>
                                </span>
                            </AccordionHeader>
                            <AccordionPanel>{renderGrid(protocol.items)}</AccordionPanel>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        );
    };

    // ── Group by settlement ────────────────────────────────────────────────
    const { settlementGroups, unlinkedConsumptions, unlinkedSummary } = useMemo(() => {
        const bySettlement = new Map<number, { id: number; name: string; identifier: string; items: PractitionerConsumption[] }>();
        const unlinked: PractitionerConsumption[] = [];

        filteredConsumptions.forEach((c) => {
            if (c.relatedSettlementId) {
                const existing = bySettlement.get(c.relatedSettlementId);
                if (existing) {
                    existing.items.push(c);
                } else {
                    bySettlement.set(c.relatedSettlementId, {
                        id: c.relatedSettlementId,
                        name: c.relatedSettlementName || c.relatedSettlementIdentifier || `#${c.relatedSettlementId}`,
                        identifier: c.relatedSettlementIdentifier || '',
                        items: [c],
                    });
                }
            } else {
                unlinked.push(c);
            }
        });

        return {
            settlementGroups: Array.from(bySettlement.values()).map((group) => ({
                ...group,
                summary: summarizeConsumptions(group.items),
            })),
            unlinkedConsumptions: unlinked,
            unlinkedSummary: summarizeConsumptions(unlinked),
        };
    }, [filteredConsumptions]);



    // ── Reusable grid renderer ─────────────────────────────────────────────
    const renderGrid = (items: PractitionerConsumption[]) => (
        <DataGrid
            items={items}
            columns={columns.filter(Boolean) as TableColumnDefinition<PractitionerConsumption>[]}
            sortable
            selectionMode="multiselect"
            selectedItems={selectedRows}
            onSelectionChange={onSelectionChange}
            getRowId={(item) => item.id}
            resizableColumns={false}
            columnSizingOptions={columnSizingOptions}
            focusMode="composite"
        >
            <DataGridHeader>
                <DataGridRow selectionCell={{ checkboxIndicator: { 'aria-label': 'Select all rows' } }}>
                    {({ renderHeaderCell }) => (
                        <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                    )}
                </DataGridRow>
            </DataGridHeader>
            <DataGridBody<ProtocolConsumption>>
                {({ item, rowId }) => (
                    <DataGridRow<ProtocolConsumption>
                        className={ComponentFunctions.GetRowClassByStatus(item.status)}
                        key={rowId}
                        selectionCell={{ checkboxIndicator: { 'aria-label': 'Select row' } }}
                    >
                        {({ renderCell }) => (
                            <DataGridCell onClick={(e: any) => {
                                e.stopPropagation();
                                setSelectedConsumption(item as any);
                                setIsInformationPanelOpen(true);
                            }}>{renderCell(item)}</DataGridCell>
                        )}
                    </DataGridRow>
                )}
            </DataGridBody>
        </DataGrid>
    );

    return (
        <>
            <div ref={toolbarRef} className={`PractitionerConsumptionListToolbar${isToolbarSticky ? ' is-sticky' : ''}`}>
                <PractitionerConsumptionMenuComponent
                    Filters={currentFilters}
                    PractitionerId={props.PractitionerId}
                    SelectedConsumptions={props.Consumptions?.filter((item: PractitionerConsumption) => Array.from(selectedRows).includes(item.id)) || []}
                    isChangingStatus={false} // TODO: wire to redux if needed
                    isAddingConsumption={false} // TODO: wire to redux if needed
                    onRequestUpdate={onRequestUpdate}
                    onAddConsumption={() => setIsConsumptionFormOpen(true)}
                    ChangeStatus={props.ChangeStatus}
                />
                {props.PractitionerId && (
                    <>
                        <Separator vertical style={{ height: '28px' }} />
                        <PractitionerConsumptionFilterComponent
                            filterText={filterText || ''}
                            setFilterText={setFilterText}
                            selectedProtocols={selectedProtocols}
                            setSelectedProtocols={setSelectedProtocols}
                            selectedCurrency={selectedCurrency}
                            setSelectedCurrency={setSelectedCurrency}
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            selectedDateRange={currentFilters.SelectedDateRange}
                            setSelectedDateRange={(dateRange: [Date | undefined, Date | undefined]) => {
                                setCurrentFilter({
                                    ...currentFilters,
                                    SelectedDateRange: dateRange
                                });
                            }}
                            onChangeFilters={(filters: IConsumptionsFilters) => setCurrentFilter(filters)}
                        />
                    </>
                )}
            </div>
            <div className="ConsumptionListContainer">
                {props.Practitioner && <PaginationControlComponent
                    paginationState={props.Practitioner.consumptionPagintationState}
                    showedRecords={props.Consumptions?.length ?? 0}
                    isLoading={props.isLoading}
                    setPageNumber={(newPageNumber: number) => setCurrentPage(newPageNumber)}
                    setPageSize={(newPageSize: number) => setPageSize(newPageSize)}
                />}
                <Separator />

                {/* ── Per-settlement accordion groups ── */}
                {settlementGroups.length > 0 && (
                    <Accordion
                        multiple
                        collapsible
                        className="pcg-list"
                        openItems={settlementGroups
                            .map((g) => String(g.id))
                            .filter((id) => !collapsedGroups.includes(id))}
                        onToggle={(_, data) => toggleGroup(String(data.value))}
                    >
                        {settlementGroups.map((group) => {
                            const isOpen = !collapsedGroups.includes(String(group.id));
                            return (
                            <AccordionItem
                                key={group.id}
                                value={String(group.id)}
                                className={`pcg-item${isOpen ? ' pcg-item--expanded' : ''}`}
                            >
                                <AccordionHeader expandIconPosition="end">
                                    <span className="pcg-header">
                                        <span className="pcg-headline">
                                            <Badge appearance="filled" color="brand" shape="rounded">
                                                {group.identifier || `#${group.id}`}
                                            </Badge>
                                            <span className="pcg-name" title={group.name}>{group.name}</span>
                                            <span className="pcg-count" title={`${group.items.length} consumos`}>{group.items.length}</span>
                                        </span>
                                        <GroupSummary {...group.summary} />
                                    </span>
                                </AccordionHeader>
                                <AccordionPanel>{renderProtocolGroups(String(group.id), group.items)}</AccordionPanel>
                            </AccordionItem>
                            );
                        })}
                    </Accordion>
                )}

                {/* ── Consumptions without settlement ── */}
                {unlinkedConsumptions.length > 0 && (
                    <Accordion
                        multiple
                        collapsible
                        className="pcg-list"
                        openItems={isUnlinkedOpen ? ['unlinked'] : []}
                        onToggle={() => setIsUnlinkedOpen((open) => !open)}
                    >
                        <AccordionItem
                            value="unlinked"
                            className={`pcg-item pcg-item--unlinked${isUnlinkedOpen ? ' pcg-item--expanded' : ''}`}
                        >
                            <AccordionHeader expandIconPosition="end">
                                <span className="pcg-header">
                                    <span className="pcg-headline">
                                        <Badge appearance="filled" color="warning" shape="rounded">Sin liquidación</Badge>
                                        <span className="pcg-name">Sin liquidación asociada</span>
                                        <span className="pcg-count" title={`${unlinkedConsumptions.length} consumos`}>{unlinkedConsumptions.length}</span>
                                    </span>
                                    <GroupSummary {...unlinkedSummary} />
                                </span>
                            </AccordionHeader>
                            <AccordionPanel>{renderProtocolGroups('unlinked', unlinkedConsumptions)}</AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                )}

                {props.Practitioner && <PaginationControlComponent
                    paginationState={props.Practitioner.consumptionPagintationState}
                    showedRecords={props.Consumptions?.length ?? 0}
                    isLoading={props.isLoading}
                    setPageNumber={(newPageNumber: number) => setCurrentPage(newPageNumber)}
                    setPageSize={(newPageSize: number) => setPageSize(newPageSize)}
                />}
            </div>
            <PractitionerConsumptionFormComponent
                PractitionerId={props.PractitionerId}
                isOpen={isConsumptionFormOpen}
                onClose={() => setIsConsumptionFormOpen(false)}
                onSaved={onRequestUpdate}
            />
            {selectedConsumption && 
                <PractitionerConsumptionDetailsMiniComponent 
                    PractitionerId={props.PractitionerId}
                    ConsumptionId={selectedConsumption.id}
                    isOpen={isInformationPanelOpen}
                    onClose={onInformationPanelDismiss}
                />}
            {Forms}
        </>

    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps,
)(PractitionerProtocolConsumptionListComponent as any);
